// Module: settings — «الإعدادات» (HANDOFF §3, §5.10; boards settings__currency,
// settings__branches, settings__audit, print__templates,
// settings__print-template--invoice). Flag erp.core.
//
// Module role = manager+. HANDOFF §3 lists staff's modules as المواعيد،
// المراجعون، الحسابات، المهام only, and §9 puts «settings» in the owner column,
// so staff never see «الإعدادات» (rail, menu, deep link → «اليوم»). §9 also
// gives staff «own events» in the audit log, but no board places that view
// for staff (OPEN-QUESTIONS: error--retry-and-409, settings/audit staff
// ruling); until it is ruled, the staff entry point is absent (rule 6/13).
// The server still answers /v1/erp/audit with scope "own" for staff.
// Each row and route carries its own role/flag/mode gate (menu.ts).
//
// /settings itself lands on the first row this person may open.

import { Settings } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import { gate, defineModule } from '@/app/module-types';
import { useErp } from '@/app/erp-context';
import { passes } from '@/app/gates';
import { coaRoutes } from './coa';
import { SETTINGS_MENU } from './menu';

function SettingsIndex() {
  const { gateContext } = useErp();
  const first = SETTINGS_MENU.find((m) => passes(m, gateContext));
  return <Navigate to={first?.to ?? '/today'} replace />;
}

export default defineModule({
  key: 'settings',
  label: 'الإعدادات',
  icon: Settings,
  flag: 'erp.core',
  minRole: 'manager',
  rail: 'settings',
  routes: [
    { path: '/settings', element: <SettingsIndex /> },
    {
      path: '/settings/business',
      lazy: () => import('./screens/Business').then((m) => ({ Component: m.BusinessScreen })),
      handle: gate({ minRole: 'owner' }),
    },
    {
      path: '/settings/branches',
      lazy: () => import('./screens/Branches').then((m) => ({ Component: m.BranchesScreen })),
      handle: gate({ minRole: 'manager', flag: 'erp.branches' }),
    },
    {
      path: '/settings/currency',
      lazy: () => import('./screens/Currency').then((m) => ({ Component: m.CurrencyScreen })),
      handle: gate({ minRole: 'manager' }),
    },
    {
      path: '/settings/print',
      lazy: () => import('./screens/PrintTemplates').then((m) => ({ Component: m.PrintIndexRoute })),
      handle: gate({ minRole: 'owner' }),
    },
    {
      path: '/settings/print/numbering',
      lazy: () => import('./screens/Numbering').then((m) => ({ Component: m.NumberingScreen })),
      handle: gate({ minRole: 'owner' }),
    },
    {
      path: '/settings/print/templates/:key',
      lazy: () => import('./screens/PrintTemplateEditor').then((m) => ({ Component: m.PrintTemplateEditorRoute })),
      handle: gate({ minRole: 'owner', flag: 'erp.print' }),
    },
    // «شجرة الحسابات» (owner, erp.accounts) — the menu row is in SETTINGS_MENU.
    ...coaRoutes,
    {
      path: '/settings/audit',
      lazy: () => import('./screens/Audit').then((m) => ({ Component: m.AuditScreen })),
      handle: gate({ minRole: 'manager' }),
    },
    {
      path: '/settings/subscription',
      lazy: () => import('./screens/Subscription').then((m) => ({ Component: m.SubscriptionScreen })),
      handle: gate({ minRole: 'owner', modes: ['online'] }),
    },
    {
      path: '/settings/features',
      lazy: () => import('./screens/Features').then((m) => ({ Component: m.FeaturesScreen })),
      handle: gate({ minRole: 'owner', modes: ['online'] }),
    },
    {
      path: '/settings/appearance',
      lazy: () => import('./screens/Appearance').then((m) => ({ Component: m.AppearanceScreen })),
      handle: gate({ minRole: 'staff', flag: 'ui.dark-mode' }),
    },
  ],
  menu: SETTINGS_MENU,
});
